import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './AdminTourOptimizer.css';

const API = '/api/admin/tour-optimizer';
const GROUPS = [
  { title: 'Basic', fields: ['title', 'slug', 'categories', 'tour_type', 'sort_order'] },
  { title: 'Content', fields: ['short_description', 'description', 'intro_html', 'local_expertise_html'] },
  { title: 'SEO', fields: ['seo_title', 'seo_description', 'seo_keywords', 'optimized'] },
  { title: 'AI / GEO & Highlights', fields: ['hero_facts_json', 'experience_highlights_json', 'faq_json', 'related_guides_json'] },
  { title: 'Hero & Gallery', fields: ['image_url', 'image_alt', 'gallery'] },
  { title: 'Itinerary & Tour Details', fields: ['itinerary_json', 'duration', 'locations', 'what_is_included', 'good_to_know', 'map_embed'] },
  { title: 'Booking & Operator', fields: ['price', 'is_external', 'external_type', 'external_iframe_url', 'external_scripts', 'operator_name', 'booking_partner', 'booking_note'] },
  { title: 'Structured Data', fields: ['structured_data'] },
];
const JSON_FIELDS = new Set(['gallery','hero_facts_json','experience_highlights_json','itinerary_json','faq_json','related_guides_json','structured_data','locations','categories','external_scripts']);
const BOOL_FIELDS = new Set(['is_external', 'optimized']);
const INT_FIELDS = new Set(['price', 'sort_order']);
const LONG_FIELDS = new Set(['description','intro_html','local_expertise_html','short_description','seo_description','seo_keywords','what_is_included','good_to_know','map_embed','booking_note','structured_data','external_scripts']);
const BOOKING_FIELDS = new Set(['price','locations','duration','tour_type','is_external','external_iframe_url','external_scripts','external_type','operator_name','booking_partner','booking_note','what_is_included','good_to_know']);
const AI_FIELDS = ['title','description','intro_html','local_expertise_html','short_description','faq_json','experience_highlights_json','related_guides_json','seo_title','seo_description','seo_keywords'];
const SCORE_LABELS = {
  content_quality:'Content quality',search_intent:'Search intent',seo:'SEO',geo_ai_readiness:'AI / GEO readiness',
  trust_sources:'Trust & sources',structure_readability:'Structure & readability',internal_linking:'Internal linking',structured_data:'Structured data'
};
const pretty = (key, value) => {
  if (JSON_FIELDS.has(key)) {
    try { return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : (value ?? []), null, 2); }
    catch (_) { return value == null ? '' : String(value); }
  }
  if (BOOL_FIELDS.has(key)) return Boolean(value);
  return value == null ? '' : String(value);
};
const label = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, x => x.toUpperCase()).replace(/Json$/, 'JSON');
const getToken = () => localStorage.getItem('kt_admin_token') || '';
const normalizeCompare = (v) => JSON.stringify(v);
const downloadJson = (name, data) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data,null,2)],{type:'application/json'}));
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
async function readError(res) {
  try { const body = await res.json(); return typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body); }
  catch (_) { return `HTTP ${res.status}`; }
}

export default function AdminTourOptimizer() {
  const token = getToken();
  const [tours,setTours] = useState([]);
  const [search,setSearch] = useState('');
  const [selected,setSelected] = useState('');
  const [editor,setEditor] = useState(null);
  const [form,setForm] = useState({});
  const [versions,setVersions] = useState([]);
  const [aiPackage,setAiPackage] = useState(null);
  const [raw,setRaw] = useState('');
  const [aiPreview,setAiPreview] = useState(null);
  const [accepted,setAccepted] = useState({});
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState('');
  const [notice,setNotice] = useState('');
  const [gsc,setGsc] = useState(null);
  const [gscLoading,setGscLoading] = useState(false);
  const [gscError,setGscError] = useState('');

  const request = useCallback(async (endpoint, options = {}) => {
    const res = await fetch(`${API}${endpoint}`, {
      ...options,
      headers: { Authorization:`Bearer ${token}`,
        ...(options.body ? {'Content-Type':'application/json'} : {}), ...(options.headers || {}) }
    });
    if (res.status === 401) throw new Error('Admin session expired. Log in again.');
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }, [token]);

  const perform = async (task) => {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try { await task(); } catch (err) { setError(err.message || String(err)); }
    finally { setBusy(false); }
  };

  const reload = useCallback(async (id) => {
    const [e,v] = await Promise.all([request(`/tours/${id}/editor`),request(`/tours/${id}/versions`)]);
    const fields = Object.fromEntries(Object.entries(e.tour).map(([key,value]) => [key,pretty(key,value)]));
    setEditor(e); setForm(fields); setVersions(v.versions || []);
    return e;
  }, [request]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    request('/tours').then(result => { if(active) setTours(result.tours || []); })
      .catch(err => { if(active) setError(err.message); });
    return () => { active = false; };
  }, [request,token]);

  // Search Console is a separate, optional read-only panel. A failure must
  // never prevent tour editing, AI review, or publishing.
  useEffect(() => {
    if (!selected || !token) {
      setGsc(null); setGscError(''); setGscLoading(false);
      return;
    }
    let active = true;
    setGsc(null);
    setGscError('');
    setGscLoading(true);
    request(`/tours/${selected}/gsc`)
      .then(report => { if (active) setGsc(report); })
      .catch(err => { if (active) setGscError(err.message || String(err)); })
      .finally(() => { if (active) setGscLoading(false); });
    return () => { active = false; };
  }, [selected, token, request]);

  useEffect(() => {
    if (!selected || !token) { setEditor(null); setForm({}); return; }
    let active = true;
    Promise.all([request(`/tours/${selected}/editor`),request(`/tours/${selected}/versions`)]).then(([e,v]) => {
      if (!active) return;
      setEditor(e);
      setForm(Object.fromEntries(Object.entries(e.tour).map(([key,value]) => [key,pretty(key,value)])));
      setVersions(v.versions || []);
    }).catch(err => { if(active) setError(err.message); });
    return () => { active = false; };
  }, [selected,token,request]);

  const changed = useMemo(() => {
    if (!editor) return [];
    return editor.editable_fields.filter(field => normalizeCompare(form[field]) !== normalizeCompare(pretty(field,editor.tour[field])));
  }, [editor,form]);
  const filtered = useMemo(() => tours.filter(t => `${t.title} ${t.slug} ${t.id}`.toLowerCase().includes(search.toLowerCase())),[tours,search]);
  const setField = (field,value) => setForm(current => ({...current,[field]:value}));

  const buildChanges = (fields) => {
    const changes = {};
    for (const key of fields) {
      let value = form[key];
      if (JSON_FIELDS.has(key)) {
        try { value = JSON.parse(value); }
        catch (_) { throw new Error(`${label(key)} contains invalid JSON`); }
      } else if (INT_FIELDS.has(key)) {
        if (!/^-?\d+$/.test(String(value))) throw new Error(`${label(key)} must be an integer`);
        value = Number(value);
      } else if (BOOL_FIELDS.has(key)) value = Boolean(value);
      changes[key] = value;
    }
    return changes;
  };

  const saveManual = () => perform(async () => {
    if (!editor || !changed.length) return;
    if (changed.some(f => BOOKING_FIELDS.has(f)) && !window.confirm('You are changing booking or operating facts. Verify the price, capacity-related settings and partner booking details. Continue?')) return;
    const body = {tour_id:editor.tour.id,slug:editor.tour.slug,record_hash:editor.record_hash,changes:buildChanges(changed)};
    const out = await request('/manual-save',{method:'POST',body:JSON.stringify(body)});
    await reload(selected);
    setAiPreview(null); setAiPackage(null);
    setNotice(`Saved ${out.changed_fields.length} fields as version #${out.version_id}. Live HTML is unchanged.`);
  });

  const exportPackage = () => perform(async () => {
    const pack = await request(`/tours/${selected}/package`);
    setAiPackage(pack);
    downloadJson(`tour-${editor.tour.slug}-package.json`,pack);
    setNotice('AI package downloaded. Upload it to your ChatGPT Project.');
  });

  const validateAi = () => perform(async () => {
    if (!editor) return;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Expected a JSON object');
    if (Number(data.tour_id) !== Number(selected) || data.slug !== editor.tour.slug) throw new Error('Imported JSON belongs to another tour');
    const preview = await request('/preview',{method:'POST',body:JSON.stringify(data)});
    const picks = Object.fromEntries((preview.changed_fields || []).map(key => [key,true]));
    setAccepted(picks); setAiPreview({data,preview});
    setNotice('AI proposal validated. Review and select changes below. Nothing has been saved.');
  });

  const adoptAi = () => {
    if (!aiPreview) return;
    setForm(current => {
      const next = {...current};
      for (const key of aiPreview.preview.changed_fields || []) {
        if (accepted[key]) next[key] = pretty(key,aiPreview.data.rewritten_tour[key]);
      }
      return next;
    });
    setNotice('Selected AI suggestions copied into the editor. Review and save changes separately.');
  };

  const saveAi = () => perform(async () => {
    if (!editor || !aiPreview) return;
    // AI-review save can be done after manual adjustments to the editorial fields.
    // All unrelated changed booking fields must be saved through the manual action first.
    if (changed.some(field => !AI_FIELDS.includes(field))) throw new Error('Save non-editorial changes separately before saving AI review.');
    const rewrites = {...aiPreview.data.rewritten_tour};
    for (const key of AI_FIELDS) {
      if (JSON_FIELDS.has(key)) {
        try { rewrites[key] = JSON.parse(form[key]); } catch (_) { throw new Error(`${label(key)}: invalid JSON`); }
      } else rewrites[key] = form[key];
    }
    const payload = {...aiPreview.data,rewritten_tour:rewrites};
    await request('/preview',{method:'POST',body:JSON.stringify(payload)});
    if (!window.confirm('Save reviewed AI content and score to the database? This does not publish the tour.')) return;
    const out = await request('/import',{method:'POST',body:JSON.stringify(payload)});
    await reload(selected); setAiPreview(null); setAiPackage(null); setRaw('');
    setNotice(`AI review saved as version #${out.version_id}. Publish separately.`);
  });

  const publish = () => perform(async () => {
    if (!editor) return;
    if (changed.length) throw new Error('You have unsaved changes. Save or discard them before publishing.');
    if (!window.confirm(`Publish only /tours/${editor.tour.slug}/ and shared tour data/sitemap?`)) return;
    const result = await request(`/tours/${selected}/publish`,{method:'POST'});
    setNotice(`Published ${result.url}`);
  });

  if (!token) return <main className="tao-v2"><h1>Tour AI Optimizer</h1><p>Admin login required.</p><Link to="/secure-admin-42/login">Go to login</Link></main>;

  return <main className="tao-v2">
    <div className="tao-top"><div><h1>Tour AI Optimizer</h1><p>Edit all tour fields, review AI proposals and publish only when ready.</p></div><Link className="tao-secondary" to="/secure-admin-42">← Dashboard</Link></div>
    <p className="tao-note">Database changes appear through the API immediately. Publish / Rebuild Live separately to update prerendered tour HTML, shared data and sitemap.</p>
    {error && <div role="alert" className="tao-error">{error}</div>}
    {notice && <div role="status" className="tao-success">{notice}</div>}
    <div className="tao-columns">
      <aside className="tao-sidebar"><input aria-label="Search tours" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tours..."/><p>{filtered.length} tours</p><div className="tao-tour-list">{filtered.map(t=><button type="button" className={String(t.id)===String(selected)?'selected':''} key={t.id} onClick={()=>{if(changed.length&&!window.confirm('Discard unsaved changes?'))return;setSelected(String(t.id));setRaw('');setAiPreview(null);setAiPackage(null);setError('');setNotice('');}}><strong>{t.title}</strong><small>/tours/{t.slug}/</small><small>#{t.id} · {t.price} SEK</small></button>)}</div></aside>
      <div className="tao-main">
        {!editor ? <section className="tao-card"><h2>Select a tour</h2><p>Choose a tour on the left to see all stored fields and AI review controls.</p></section> : <>
          <div className="tao-tour-head"><div><h2>{editor.tour.title}</h2><small>ID {editor.tour.id} · /tours/{editor.tour.slug}/</small></div><a className="tao-secondary" href={`/tours/${editor.tour.slug}/`} target="_blank" rel="noopener noreferrer">Open live ↗</a></div>
          <section className="tao-card tao-gsc-panel" aria-label="Google Search Console statistics">
            <div className="tao-gsc-header">
              <div>
                <h2>Google Search Console</h2>
                <p className="tao-muted">Organic Google web search · exact tour URL · read-only</p>
              </div>
              {gsc?.status === 'verified' && <span className="tao-gsc-verified">Verified</span>}
            </div>
            {gscLoading && <p role="status">Loading Search Console performance…</p>}
            {gscError && <p role="alert" className="tao-error">GSC data unavailable: {gscError}. Tour editing still works.</p>}
            {!gscLoading && !gscError && gsc && gsc.status !== 'verified' &&
              <p className="tao-muted">No verified GSC report is available for this tour.</p>}
            {gsc?.status === 'verified' && <>
              <div className="tao-gsc-controls">
                <span>Data through {gsc.report_end_date} (UTC cutoff)</span>
                <span>Cached up to 30 minutes</span>
              </div>
              <div className="tao-gsc-periods">
                {['28','90'].map(days => {
                  const current = gsc.windows?.[days]?.current;
                  const previous = gsc.windows?.[days]?.previous;
                  if (!current) return null;
                  return <div className="tao-gsc-period" key={days}>
                    <h3>Last {days} days</h3>
                    <small>{current.start_date} – {current.end_date}</small>
                    <div className="tao-gsc-metrics">
                      <div><small>Clicks</small><strong>{Number(current.clicks).toLocaleString('sv-SE')}</strong></div>
                      <div><small>Impressions</small><strong>{Number(current.impressions).toLocaleString('sv-SE')}</strong></div>
                      <div><small>CTR</small><strong>{Number(current.ctr_percent).toFixed(2)}%</strong></div>
                      <div><small>Avg. position</small><strong>{current.position == null ? '—' : Number(current.position).toFixed(1)}</strong></div>
                    </div>
                    {previous && <p className="tao-muted tao-gsc-previous">
                      Previous {days}d: {previous.clicks} clicks · {previous.impressions} impressions · {Number(previous.ctr_percent).toFixed(2)}% CTR · position {previous.position == null ? '—' : Number(previous.position).toFixed(1)}
                    </p>}
                  </div>;
                })}
              </div>
              <details className="tao-gsc-queries">
                <summary>Search queries (90 days) · {gsc.queries_90d?.length ?? 0} reported</summary>
                {gsc.queries_90d?.length ? <div className="tao-gsc-table-wrap">
                  <table><thead><tr><th>Query</th><th>Clicks</th><th>Impressions</th><th>CTR</th><th>Position</th></tr></thead>
                    <tbody>{gsc.queries_90d.slice(0,100).map((item,i)=><tr key={`${item.query}-${i}`}>
                      <td>{item.query}</td><td>{item.clicks}</td><td>{item.impressions}</td>
                      <td>{Number(item.ctr_percent).toFixed(2)}%</td>
                      <td>{Number(item.position).toFixed(1)}</td>
                    </tr>)}</tbody></table>
                </div> : <p className="tao-muted">Google did not return any reportable queries for this exact URL.</p>}
                <p className="tao-muted">Search Console omits some anonymized queries; query totals may not match page totals.</p>
              </details>
              <p className="tao-muted">{gsc.report_note}</p>
              <p className="tao-muted">GSC data is included automatically when you download or copy the AI Rewrite Package.</p>
            </>}
          </section>
          <section className="tao-card"><div className="tao-card-title"><h2>AI Tour Review</h2>{versions.find(v=>v.score_json?.overall_score)!=null && <strong className="tao-score">{versions.find(v=>v.score_json?.overall_score)?.score_json?.overall_score}/10</strong>}</div>
            {versions.find(v=>v.score_json?.scores) ? <div className="tao-scores">{Object.entries(SCORE_LABELS).map(([key,name])=><div key={key}><small>{name}</small><strong>{versions.find(v=>v.score_json?.scores)?.score_json?.scores?.[key] ?? '—'}</strong></div>)}</div>:<p>No AI score saved yet for this tour.</p>}
            <div className="tao-buttons"><button disabled={busy} onClick={exportPackage}>Download Rewrite Package</button><button className="tao-secondary" disabled={busy} onClick={()=>perform(async()=>{const p=await request(`/tours/${selected}/package`);setAiPackage(p);await navigator.clipboard.writeText(JSON.stringify(p,null,2));setNotice('AI package copied.');})}>Copy package</button></div>
            <details className="tao-import"><summary>Import AI rewrite & score JSON</summary><p>Review changes field by field before saving. Only the eleven editorial fields may be changed by AI.</p>
              <input type="file" accept=".json,application/json" aria-label="Import AI JSON" onChange={async e=>{const f=e.target.files?.[0];if(!f)return;if(f.size>1024*1024){setError('File exceeds 1 MB');return;}setRaw(await f.text());setAiPreview(null);}}/>
              <textarea rows={7} value={raw} onChange={e=>{setRaw(e.target.value);setAiPreview(null);}} placeholder='{"tour_id":1,"slug":"...","source_hash":"...","rewritten_tour":{},"new_score":{}}'/>
              <div className="tao-buttons"><button disabled={busy||!raw.trim()} onClick={validateAi}>Validate & compare</button></div>
              {aiPreview && <div className="tao-ai-diff"><h3>AI proposed score: {aiPreview.preview.new_score.overall_score}/10</h3>
                {(aiPreview.preview.changed_fields || []).length===0?<p>No editorial changes detected.</p>:(aiPreview.preview.changed_fields || []).map(key=><div key={key} className="tao-diff-row"><label><input type="checkbox" checked={Boolean(accepted[key])} onChange={e=>setAccepted(s=>({...s,[key]:e.target.checked}))}/> Accept {label(key)}</label><div className="tao-diff-cols"><div><small>Current database</small><pre>{pretty(key,editor.tour[key])}</pre></div><div><small>AI proposal</small><pre>{pretty(key,aiPreview.data.rewritten_tour[key])}</pre></div></div></div>)}
                <div className="tao-buttons"><button onClick={adoptAi}>Apply selected to editor</button><button className="tao-secondary" disabled={busy} onClick={saveAi}>Save editor content + AI score</button></div>
                <p className="tao-muted">To use the AI suggestions: apply selected proposals, adjust any text in the editor below, then save the AI review. This does not publish HTML.</p>
              </div>}
            </details>
          </section>
          {GROUPS.map(group=><section key={group.title} className="tao-card"><h2>{group.title}</h2><div className="tao-fields">{group.fields.filter(key=>key==='slug'||editor.editable_fields.includes(key)).map(key=>{
            const locked = key==='slug';const isJson=JSON_FIELDS.has(key);const isLong=LONG_FIELDS.has(key)||isJson;
            return <div key={key} className={`tao-field ${isLong?'wide':''} ${changed.includes(key)?'modified':''}`}><label htmlFor={`tao-${key}`}>{label(key)}{locked && ' (locked)'}{changed.includes(key)&&<span> · Modified</span>}</label>
              {BOOL_FIELDS.has(key)?<label className="tao-check"><input id={`tao-${key}`} type="checkbox" checked={Boolean(form[key])} onChange={e=>setField(key,e.target.checked)}/> Enabled</label>:
              isLong?<textarea id={`tao-${key}`} rows={isJson?7:5} value={form[key] ?? ''} onChange={e=>setField(key,e.target.value)} readOnly={locked} spellCheck={!isJson}/>:
              <input id={`tao-${key}`} value={form[key] ?? ''} readOnly={locked} onChange={e=>setField(key,e.target.value)}/>}
              {isJson && <small className="tao-muted">JSON {(()=>{try{JSON.parse(form[key]);return '✓ valid';}catch(_){return '⚠ invalid';}})()}</small>}
              {key==='image_url' && form[key] && <img className="tao-image-preview" src={form[key]} alt="Current tour hero preview" loading="lazy"/>}
              {BOOKING_FIELDS.has(key) && <small className="tao-muted">Booking/operational fact – manual changes only</small>}
            </div>;
          })}</div></section>)}
          <section className="tao-card"><h2>Structured Data synchronization</h2>
            <p className="tao-muted">Update TouristTrip JSON-LD from saved tour title, description, image and price. Removes unverified InStock claims. Review before saving; this does not publish.</p>
            <button className="tao-secondary" disabled={busy || changed.length>0} onClick={()=>perform(async()=>{
              const result = await request(`/tours/${selected}/schema-preview`);
              if(result.structured_data == null){setNotice('No existing Structured Data to synchronize.');return;}
              if(!result.changed){setNotice('Structured Data already matches the saved tour.');return;}
              setField('structured_data',JSON.stringify(JSON.parse(result.structured_data),null,2));
              setNotice('Updated Structured Data in the editor. Review and click Save Changes.');
            })}>Sync JSON-LD from saved tour</button>
          </section>
          <section className="tao-card"><h2>Versions & history</h2>{versions.length?<ul className="tao-versions">{versions.map(v=><li key={v.id}>#{v.id} · {v.created_at} · {v.score_json?.type==='manual_edit'?'Manual edit':`AI review ${v.score_json?.overall_score ?? '—'}/10`}</li>)}</ul>:<p>No saved versions yet.</p>}</section>
          <div className="tao-sticky"><span>{changed.length ? `${changed.length} unsaved field${changed.length>1?'s':''}` : 'All changes saved'}</span><div className="tao-buttons"><button className="tao-secondary" disabled={busy||!changed.length} onClick={()=>{if(window.confirm('Discard all unsaved changes?'))setForm(Object.fromEntries(Object.entries(editor.tour).map(([k,v])=>[k,pretty(k,v)])));}}>Discard</button><button disabled={busy||!changed.length} onClick={saveManual}>Save Changes</button><button className="tao-publish" disabled={busy||changed.length>0} onClick={publish}>Publish / Rebuild Live</button></div></div>
        </>}
      </div>
    </div>
  </main>;
}
